import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Input,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { PartnerContractFieldDefaults } from '@interfaces/partners/contracts/responses/partner-contract.response';
import { SharedModule } from '@src/app/shared/shared.module';
import { Observable, distinctUntilChanged, map, merge, of } from 'rxjs';
import {
  DEFAULT_COMMISSION_APPROVED_CREDIT_RATE,
  DEFAULT_COMMISSION_GENERAL_RATE,
  describeInvalidPartyFields,
  isIndividualParty,
} from './partner-contract-party-form';

/**
 * Campos dos dados da parte do contrato de parceria (card 3462). O formulário vem de fora
 * (`createPartnerContractPartyForm`), para a modal "Gerar contrato" e o painel do editor usarem a
 * mesma régua de campos e validação.
 *
 * Pessoa jurídica pede CNPJ e "Razão social"; pessoa física, CPF e "Nome completo". Trocar o tipo
 * apaga o documento do outro tipo (o backend recusa os dois juntos). O signatário da Legatus não é
 * campo: vem da Parametrização e aparece só para leitura.
 */
@Component({
  selector: 'app-partner-contract-party-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SharedModule],
  templateUrl: './partner-contract-party-form.component.html',
  styleUrls: ['./partner-contract-party-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PartnerContractPartyFormComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  @Input({ required: true }) form!: FormGroup;
  /** Uma coluna (painel lateral) ou duas (modal). */
  @Input() layout: 'single' | 'double' = 'double';
  /**
   * Trava os campos (minuta finalizada ou sem permissão de editar). Precisa ir explícito: o
   * `app-input` em modo `controlName` não enxerga o `disable()` do FormGroup.
   */
  @Input() readonly = false;
  /**
   * Signatário da Legatus gravado na minuta (só leitura). `undefined` = ainda não gerado (a modal
   * explica de onde ele vem); `null`/vazio = a Parametrização estava sem ninguém.
   */
  @Input() legatusSignatoryName: string | null | undefined = undefined;

  /**
   * Padrões dos percentuais do Anexo I (card 3643), vindos da minuta (`fieldDefaults`). Sem eles,
   * valem os mesmos 10% e 20% do backend. Servem para a dica "Padrão 10%" e a etiqueta "Fora do
   * padrão"; quem marca a minuta para aprovação é o backend, ao gravar.
   */
  @Input() set fieldDefaults(value: PartnerContractFieldDefaults | null | undefined) {
    this.defaults.set({
      commissionGeneralRate: value?.commissionGeneralRate ?? DEFAULT_COMMISSION_GENERAL_RATE,
      commissionApprovedCreditRate:
        value?.commissionApprovedCreditRate ?? DEFAULT_COMMISSION_APPROVED_CREDIT_RATE,
    });
    this.refreshOutOfDefault();
  }

  readonly defaults = signal<PartnerContractFieldDefaults>({
    commissionGeneralRate: DEFAULT_COMMISSION_GENERAL_RATE,
    commissionApprovedCreditRate: DEFAULT_COMMISSION_APPROVED_CREDIT_RATE,
  });

  /** Percentuais diferentes do padrão, pelo valor digitado (ainda não salvo). */
  readonly outOfDefault = signal({
    commissionGeneralRate: false,
    commissionApprovedCreditRate: false,
  });

  /** Pessoa física: troca rótulos e o documento. */
  readonly individual = signal(false);

  /**
   * O que impede salvar, campo a campo. Sem isto o botão da modal só ficava desabilitado: um e-mail
   * pré-preenchido em formato inválido (ex.: domínio com "_") travava a tela sem explicação.
   */
  invalidFields$!: Observable<string[]>;

  ngOnInit(): void {
    this.individual.set(isIndividualParty(this.form));

    this.invalidFields$ = merge(of(null), this.form.statusChanges, this.form.valueChanges).pipe(
      map(() => describeInvalidPartyFields(this.form))
    );

    this.refreshOutOfDefault();
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshOutOfDefault());

    const typeControl = this.form.get('partyType');
    typeControl?.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(type => {
        const individual = type === 'Individual';
        this.individual.set(individual);
        // Só um documento por contrato: apaga o do outro tipo.
        this.form.get(individual ? 'partyCnpj' : 'partyCpf')?.setValue('');
      });
  }

  /** Recalcula a etiqueta "Fora do padrão" de cada percentual (só comparação, sem cálculo). */
  private refreshOutOfDefault(): void {
    if (!this.form) return;
    const defaults = this.defaults();
    const differs = (name: keyof PartnerContractFieldDefaults): boolean => {
      const raw = this.form.get(name)?.value;
      if (raw === null || raw === undefined || raw === '') return false;
      return Number(raw) !== defaults[name];
    };
    this.outOfDefault.set({
      commissionGeneralRate: differs('commissionGeneralRate'),
      commissionApprovedCreditRate: differs('commissionApprovedCreditRate'),
    });
  }
}
